const clamp = (number, minimum, maximum) => Math.min(maximum, Math.max(minimum, number));

class VccPrecisionGallery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.abort = new AbortController();
    this.gallery = this.closest('media-gallery');
    this.lens = this.querySelector('[data-vcc-lens]');
    this.lensBadge = this.querySelector('[data-vcc-lens-badge]');
    this.panel = this.querySelector('[data-vcc-panel]');
    this.panelTrigger = this.querySelector('[data-vcc-action="panel"]');
    this.dialog = this.querySelector('[data-vcc-ultra]');
    this.ultraShell = this.querySelector('[data-vcc-ultra-shell]');
    this.viewport = this.querySelector('[data-vcc-ultra-viewport]');
    this.ultraImage = this.querySelector('[data-vcc-ultra-image]');
    this.urls = JSON.parse(this.querySelector('[data-vcc-media-json]')?.textContent || '[]');
    this.lensReadySources = new Set();
    this.lensLoadingSources = new Map();
    this.ultraReadySources = new Set();
    this.ultraLoadingSources = new Map();
    this.ultraPreloadTimer = 0;
    this.desktopThumbBar = this.querySelector('[data-vcc-desktop-thumb-bar]') || this.gallery?.querySelector('[data-vcc-desktop-thumb-bar]');
    this.mobileThumbBar = this.querySelector('[data-vcc-mobile-thumb-bar]') || this.gallery?.querySelector('[data-vcc-mobile-thumb-bar]');
    this.zoom = clamp(Number(this.dataset.lensZoom || 2), 2, 8);
    this.lensEnabled = this.dataset.lensEnabled === 'true';
    this.lensSizeMode = this.dataset.lensSize || 'small';
    this.lensShape = this.dataset.lensShape || 'circle';
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    this.imageIndex = 0;
    this.detailEnabled = false;
    this.detailStrength = clamp(Number(this.dataset.detailStrength || 100), 0, 200) / 100;
    this.backgroundIndex = 0;
    this.ultraPointers = new Map();
    this.touchLensPointer = null;
    this.positionFrame = 0;
    this.activeSlide = null;
    this.ultraHistoryOpen = false;
    this.ultraSkipHistoryClose = false;
    this.bind();
    this.mountThumbBars();
    this.syncLensControls();
    this.updateCounter(0);
    this.updateStampPreview();
    requestAnimationFrame(() => {
      this.mountThumbBars();
      this.positionTools();
      this.alignThumbnailRail(0, true);
    });
    setTimeout(() => {
      this.mountThumbBars();
      this.positionTools();
      this.alignThumbnailRail(0, true);
    }, 500);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.hideLens();
    [this.desktopThumbBar, this.mobileThumbBar].forEach((bar) => {
      if (bar && !this.contains(bar)) bar.remove();
    });
    if (this.dialog?.open) {
      this.ultraSkipHistoryClose = true;
      this.dialog.close();
    }
    this.positionObserver?.disconnect?.();
    if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
    if (this.ultraPreloadTimer) window.clearTimeout(this.ultraPreloadTimer);
    delete this.dataset.initialized;
  }

  bind() {
    const signal = this.abort.signal;
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    [this.desktopThumbBar, this.mobileThumbBar].forEach((bar) => {
      bar?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-vcc-action]');
        if (!button || !bar.contains(button)) return;
        event.stopPropagation();
        this.onClick(event);
      }, { signal });
    });
    this.gallery?.addEventListener('slideshow:select', (event) => {
      this.activeSlide = event.detail?.slide || null;
      const selectedIndex = Number(event.detail?.index || 0);
      this.updateCounter(selectedIndex);
      requestAnimationFrame(() => this.alignThumbnailRail(selectedIndex, selectedIndex === 0));
      requestAnimationFrame(() => this.positionTools());
      setTimeout(() => this.positionTools(), 180);
    }, { signal });
    this.gallery?.addEventListener('pointermove', (event) => this.onLensMove(event), { signal });
    this.gallery?.addEventListener('pointerdown', (event) => this.onLensDown(event), { signal });
    this.gallery?.addEventListener('pointerup', (event) => this.onLensUp(event), { signal });
    this.gallery?.addEventListener('pointercancel', (event) => this.onLensUp(event), { signal });
    this.gallery?.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'touch') this.hideLens();
    }, { signal });
    this.gallery?.addEventListener('wheel', (event) => this.onLensWheel(event), { signal, passive: false });
    document.addEventListener('pointerdown', (event) => this.onOutsidePointer(event), { signal, capture: true });
    this.viewport?.addEventListener('wheel', (event) => this.onUltraWheel(event), { signal, passive: false });
    this.viewport?.addEventListener('pointerdown', (event) => this.onUltraPointerDown(event), { signal });
    this.viewport?.addEventListener('pointermove', (event) => this.onUltraPointerMove(event), { signal });
    this.viewport?.addEventListener('pointerup', (event) => this.onUltraPointerUp(event), { signal });
    this.viewport?.addEventListener('pointercancel', (event) => this.onUltraPointerUp(event), { signal });
    this.viewport?.addEventListener('dblclick', () => this.resetUltra(), { signal });
    this.querySelector('[data-vcc-detail-level]')?.addEventListener('change', (event) => {
      this.detailStrength = Number(event.target.value || 1);
      this.applyDetail();
    }, { signal });
    const hold = this.querySelector('[data-vcc-action="hold-original"]');
    hold?.addEventListener('pointerdown', () => this.ultraImage.dataset.detail = 'false', { signal });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => hold?.addEventListener(type, () => this.applyDetail(), { signal }));
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.closeUltra();
    }, { signal });
    this.dialog?.addEventListener('close', () => {
      const stepBack = this.ultraHistoryOpen && !this.ultraSkipHistoryClose;
      this.ultraHistoryOpen = false;
      this.ultraSkipHistoryClose = false;
      this.resetUltra();
      this.launcher?.focus?.({ preventScroll: true });
      if (stepBack && window.history) window.history.back();
    }, { signal });
    const scheduleLayout = () => this.queuePositionTools();
    this.precisionStylesheetLinks().forEach((link) => {
      if (!link.sheet) link.addEventListener('load', scheduleLayout, { signal, once: true });
    });
    window.addEventListener('popstate', () => {
      if (this.dialog?.open) this.closeUltra({ skipHistory: true });
      else this.ultraHistoryOpen = false;
    }, { signal });
    window.addEventListener('keydown', (event) => this.onKey(event), { signal });
    window.addEventListener('resize', scheduleLayout, { signal, passive: true });
    window.addEventListener('orientationchange', scheduleLayout, { signal, passive: true });
    window.visualViewport?.addEventListener('resize', scheduleLayout, { signal, passive: true });
    window.visualViewport?.addEventListener('scroll', scheduleLayout, { signal, passive: true });
    if ('ResizeObserver' in window && this.gallery) {
      this.positionObserver = new ResizeObserver(scheduleLayout);
      this.positionObserver.observe(this.gallery);
      this.gallery.querySelectorAll('slideshow-component, slideshow-container, slideshow-slides').forEach((element) => {
        this.positionObserver.observe(element);
      });
    }
  }

  queuePositionTools() {
    if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
    this.positionFrame = requestAnimationFrame(() => {
      this.positionFrame = 0;
      this.mountThumbBars();
      this.positionTools();
      requestAnimationFrame(() => this.positionTools());
      setTimeout(() => this.positionTools(), 180);
    });
  }

  mountThumbBars() {
    if (!this.gallery) return;
    const position = this.dataset.thumbBarPosition === 'above' ? 'above' : 'below';
    const mount = (bar, rail) => {
      if (!bar || !rail) return;
      const controls = rail.closest('slideshow-controls') || rail.parentElement;
      if (!controls) return;
      bar.dataset.vccThumbBarPosition = position;
      if (position === 'above') controls.insertBefore(bar, rail);
      else rail.after(bar);
      bar.dataset.vccThumbBarMounted = 'true';
      if (!this.hasAttribute('data-vcc-layout-pending')) bar.removeAttribute('data-vcc-thumb-bar-pending');
    };
    const desktopRail = this.gallery.querySelector('slideshow-controls:not(.media-gallery__mobile-controls) .slideshow-controls__thumbnails-container');
    const mobileRail = this.gallery.querySelector('slideshow-controls.media-gallery__mobile-controls .slideshow-controls__thumbnails-container');
    mount(this.desktopThumbBar, desktopRail);
    mount(this.mobileThumbBar, mobileRail || desktopRail);
  }

  onOutsidePointer(event) {
    if (!this.panel?.hidden && !this.lensEnabled && !this.panel.contains(event.target) && !this.panelTrigger?.contains(event.target)) this.setPanel(false);
    this.querySelectorAll('[data-vcc-share-menu], [data-vcc-ultra-share-menu], [data-vcc-save-menu]').forEach((menu) => {
      if (!menu.hidden && !menu.contains(event.target) && !event.target.closest('[data-vcc-action="share"], [data-vcc-action="ultra-share"], [data-vcc-action="save-menu"]')) menu.hidden = true;
    });
  }

  onClick(event) {
    const button = event.target.closest('[data-vcc-action]');
    if (!button) return;
    const action = button.dataset.vccAction;
    if (action === 'panel') {
      if (matchMedia('(max-width: 749px)').matches) return;
      this.lensEnabled = true;
      this.dataset.lensEnabled = 'true';
      this.setPanel(true);
      this.warmActiveLensImage();
      this.syncLensControls();
    }
    if (action === 'panel-close') {
      this.lensEnabled = false;
      this.dataset.lensEnabled = 'false';
      this.hideLens();
      this.setPanel(false);
      this.syncLensControls();
    }
    if (action === 'share') this.toggleMenu(this.querySelector('[data-vcc-share-menu]'), button);
    if (action === 'ultra-share') this.toggleMenu(this.querySelector('[data-vcc-ultra-share-menu]'), button);
    if (action === 'save-menu') this.toggleMenu(this.querySelector('[data-vcc-save-menu]'), button);
    if (action === 'copy') this.copyShareLink(button);
    if (action === 'facebook') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.shareUrl())}`, '_blank', 'noopener,noreferrer,width=700,height=620');
    if (action === 'instagram') this.shareInstagram();
    if (action === 'ultra') this.openUltra(button);
    if (action === 'ultra-close') this.closeUltra();
    if (action === 'ultra-zoom') this.setUltraScale(Number(button.dataset.scale));
    if (action === 'ultra-in') this.setUltraScale(this.scale * 1.25);
    if (action === 'ultra-out') this.setUltraScale(this.scale / 1.25);
    if (action === 'ultra-reset') this.resetUltra();
    if (action === 'ultra-prev') this.setUltraImage(this.imageIndex - 1);
    if (action === 'ultra-next') this.setUltraImage(this.imageIndex + 1);
    if (action === 'ultra-thumb') this.setUltraImage(Number(button.dataset.index || 0));
    if (action === 'detail') {
      this.detailEnabled = !this.detailEnabled;
      button.classList.toggle('is-active', this.detailEnabled);
      this.applyDetail();
    }
    if (action === 'grid') {
      const active = this.ultraShell.dataset.grid !== 'true';
      this.ultraShell.dataset.grid = String(active);
      button.classList.toggle('is-active', active);
    }
    if (action === 'background') this.cycleBackground(button);
    if (action === 'fullscreen') this.toggleFullscreen();
    if (action === 'size-info') this.toggleSizeInfo(button);
    if (action === 'save') {
      this.querySelector('[data-vcc-save-menu]')?.setAttribute('hidden', '');
      this.saveImage(button, button.dataset.vccSaveInfo === 'true');
    }
    if (action === 'lens-zoom') {
      this.zoom = clamp(Number(button.dataset.value), 2, 8);
      this.syncLensControls();
    }
    if (action === 'lens-toggle') {
      this.lensEnabled = !this.lensEnabled;
      this.dataset.lensEnabled = String(this.lensEnabled);
      if (this.lensEnabled) {
        this.setPanel(true);
        this.warmActiveLensImage();
      } else {
        this.hideLens();
        this.setPanel(false);
      }
      this.syncLensControls();
    }
    if (action === 'lens-size') {
      this.lensSizeMode = button.dataset.value;
      this.syncLensControls();
    }
    if (action === 'lens-shape') {
      this.lensShape = button.dataset.value;
      this.syncLensControls();
    }
    if (action === 'thumb-previous') this.navigateThumbnail(-1);
    if (action === 'thumb-next') this.navigateThumbnail(1);
    if (['copy', 'instagram', 'facebook'].includes(action)) button.closest('[data-vcc-share-menu], [data-vcc-ultra-share-menu]')?.setAttribute('hidden', '');
  }

  setPanel(open) {
    if (!this.panel) return;
    this.panel.hidden = !open;
    this.panelTrigger?.setAttribute('aria-expanded', String(open));
    this.panelTrigger?.classList.toggle('is-active', open);
    this.classList.toggle('vcc-lens-active', this.lensEnabled);
    this.gallery?.classList.toggle('vcc-lens-active', this.lensEnabled);
    if (this.lensEnabled) this.warmActiveLensImage();
    this.hideLens();
    requestAnimationFrame(() => this.positionTools());
  }

  toggleMenu(menu, button) {
    if (!menu) return;
    const open = menu.hidden;
    this.querySelectorAll('[data-vcc-share-menu], [data-vcc-ultra-share-menu], [data-vcc-save-menu]').forEach((item) => item.hidden = true);
    menu.hidden = !open;
    button.setAttribute('aria-expanded', String(open));
  }

  shareUrl() {
    const canonical = document.querySelector('link[rel="canonical"]')?.href || location.href;
    const url = new URL(canonical, location.origin);
    url.searchParams.delete('preview_theme_id');
    const mediaId = this.activeImage()?.closest('.product-media')?.dataset.mediaId;
    if (mediaId) url.hash = `media-${mediaId}`;
    return url.toString();
  }

  async copyShareLink(button) {
    const text = this.shareUrl();
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      const input = document.createElement('textarea');
      input.value = text;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.append(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
    const original = button.textContent;
    button.textContent = 'Copied';
    setTimeout(() => button.textContent = original, 1200);
  }

  async shareInstagram() {
    const data = { title: document.title, text: document.title, url: this.shareUrl() };
    if (navigator.share) {
      try { await navigator.share(data); return; } catch (error) { if (error.name === 'AbortError') return; }
    }
    await this.copyShareLink(document.createElement('button'));
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
  }

  usableStage(stage) {
    if (!stage || stage.closest?.('.dialog-zoomed-gallery')) return false;
    const rect = stage.getBoundingClientRect();
    const style = getComputedStyle(stage);
    return rect.width > 40 && rect.height > 40 && style.display !== 'none' && style.visibility !== 'hidden' && rect.bottom > 0 && rect.right > 0 && rect.left < innerWidth && rect.top < innerHeight;
  }

  activeStage() {
    const selectedThumbnail = this.gallery?.querySelector('.slideshow-controls__thumbnail[aria-selected="true"], .slideshow-controls__thumbnail[aria-current="true"]');
    const thumbnails = [...(this.gallery?.querySelectorAll('.slideshow-controls__thumbnail') || [])].filter((item) => item.offsetParent !== null);
    const selectedIndex = selectedThumbnail ? thumbnails.indexOf(selectedThumbnail) : -1;
    const stages = [...(this.gallery?.querySelectorAll('slideshow-slide.product-media-container, .product-media-container') || [])].filter((stage) => !stage.closest('.dialog-zoomed-gallery'));
    const candidates = [
      selectedIndex >= 0 ? stages[selectedIndex] : null,
      this.activeSlide?.matches?.('.product-media-container') ? this.activeSlide : this.activeSlide?.closest?.('.product-media-container'),
      this.gallery?.querySelector('slideshow-slide.product-media-container[aria-hidden="false"]'),
      this.gallery?.querySelector('slideshow-slide.product-media-container:not([aria-hidden="true"])'),
      this.mediaStage
    ].filter(Boolean);
    return candidates.find((stage) => this.usableStage(stage)) || stages.find((stage) => this.usableStage(stage)) || stages[0] || null;
  }

  activeImage() {
    const stage = this.activeStage();
    const direct = stage?.querySelector('.product-media-container--image img, .product-media img.product-media__image, img.product-media__image');
    if (direct) return direct;
    const candidates = [...this.gallery.querySelectorAll('.product-media-container--image img, img.product-media__image')].filter((image) => !image.closest('.dialog-zoomed-gallery'));
    return candidates.find((image) => {
      const rect = image.getBoundingClientRect();
      const style = getComputedStyle(image);
      return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none' && rect.right > 0 && rect.left < innerWidth;
    }) || candidates[0];
  }

  precisionStylesheetLinks() {
    return [...document.querySelectorAll('link[rel~="stylesheet"]')]
      .filter((link) => link.href.includes('vcc-precision-gallery.css'));
  }

  precisionStylesReady() {
    const links = this.precisionStylesheetLinks();
    if (!links.some((link) => link.sheet)) return false;
    const action = this.querySelector('.vcc-precision-action--ultra');
    const share = this.querySelector('.vcc-precision-share');
    if (!action || !share) return false;
    const actionStyle = getComputedStyle(action);
    const shareStyle = getComputedStyle(share);
    return ['flex', 'inline-flex'].includes(actionStyle.display) &&
      actionStyle.position === 'absolute' &&
      shareStyle.position === 'absolute' &&
      Math.abs(parseFloat(actionStyle.width) - 42) < 0.5 &&
      Math.abs(parseFloat(actionStyle.height) - 42) < 0.5;
  }

  mountedThumbBarsReady() {
    const isMobile = matchMedia('(max-width: 749px)').matches;
    const bar = isMobile ? this.mobileThumbBar : this.desktopThumbBar;
    const desktopRail = this.gallery?.querySelector(
      'slideshow-controls:not(.media-gallery__mobile-controls) .slideshow-controls__thumbnails-container'
    );
    const mobileRail = this.gallery?.querySelector(
      'slideshow-controls.media-gallery__mobile-controls .slideshow-controls__thumbnails-container'
    );
    const rail = isMobile ? (mobileRail || desktopRail) : desktopRail;
    if (!bar || !rail) return true;
    const controls = rail.closest('slideshow-controls') || rail.parentElement;
    if (!controls) return true;
    const railStyle = getComputedStyle(rail);
    const controlsStyle = getComputedStyle(controls);
    if (
      rail.getClientRects().length === 0 ||
      controls.getClientRects().length === 0 ||
      railStyle.display === 'none' || railStyle.visibility === 'hidden' ||
      controlsStyle.display === 'none' || controlsStyle.visibility === 'hidden'
    ) {
      return true;
    }
    if (bar.dataset.vccThumbBarMounted !== 'true') return false;
    const rect = bar.getBoundingClientRect();
    return [rect.left, rect.top, rect.width, rect.height].every(Number.isFinite) &&
      rect.width > 40 && rect.height >= 20;
  }

  revealPositionedLayout() {
    this.removeAttribute('data-vcc-layout-pending');
    [this.desktopThumbBar, this.mobileThumbBar].forEach((bar) => {
      if (bar?.dataset.vccThumbBarMounted === 'true') bar.removeAttribute('data-vcc-thumb-bar-pending');
    });
  }

  positionTools() {
    const tools = this.querySelector('.vcc-precision-tools');
    const image = this.activeImage();
    const activeStage = this.activeStage() || image?.closest('.product-media-container');
    const stage = activeStage || this.mediaStage;
    if (!tools || !stage || !this.gallery) return false;
    if (!this.precisionStylesReady() || !this.mountedThumbBarsReady()) return false;
    this.mediaStage = stage;
    const galleryRect = this.gallery.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    const stageWidth = Math.min(Math.max(120, stageRect.width), Math.max(120, galleryRect.width));
    const stageHeight = Math.min(Math.max(120, stageRect.height), Math.max(120, galleryRect.height));
    const stageLeft = clamp(Math.max(0, stageRect.left - galleryRect.left), 0, Math.max(0, galleryRect.width - stageWidth));
    const stageTop = clamp(Math.max(0, stageRect.top - galleryRect.top), 0, Math.max(0, galleryRect.height - stageHeight));
    const rectValues = [
      galleryRect.left, galleryRect.top, galleryRect.width, galleryRect.height,
      stageRect.left, stageRect.top, stageRect.width, stageRect.height
    ];
    const stageStyle = getComputedStyle(stage);
    if (
      !rectValues.every(Number.isFinite) ||
      galleryRect.width <= 40 || galleryRect.height <= 40 ||
      stageRect.width <= 40 || stageRect.height <= 40 ||
      stageStyle.display === 'none' || stageStyle.visibility === 'hidden'
    ) return false;
    tools.style.left = `${stageLeft}px`;
    tools.style.right = 'auto';
    tools.style.top = `${stageTop}px`;
    tools.style.width = `${stageWidth}px`;
    tools.style.height = `${stageHeight}px`;
    const note = this.querySelector('[data-vcc-scroll-note]');
    const counter = this.querySelector('[data-vcc-counter]');
    const trigger = this.panelTrigger;
    if (trigger && !matchMedia('(max-width: 749px)').matches) {
      if (trigger.closest('.vcc-precision-tools')) {
        trigger.style.left = '12px';
        trigger.style.top = 'auto';
        trigger.style.bottom = '12px';
      } else {
        trigger.style.left = `${stageLeft + 12}px`;
        trigger.style.top = `${stageTop + stageHeight - trigger.offsetHeight - 12}px`;
        trigger.style.bottom = 'auto';
      }
    }
    if (counter && this.dataset.counterPosition === 'top-left') { counter.style.left = `${stageLeft + 12}px`; counter.style.top = `${stageTop + 12}px`; }
    if (note) { note.style.left = `${stageLeft + stageWidth - note.offsetWidth - 14}px`; note.style.top = `${stageTop + stageHeight - note.offsetHeight - 12}px`; }
    if (this.panel && !this.panel.hidden) {
      this.panel.style.left = `${stageLeft + 1}px`;
      this.panel.style.width = `${stageWidth - 2}px`;
      this.panel.style.right = 'auto';
      this.panel.style.top = `${stageTop + stageHeight - this.panel.offsetHeight - 1}px`;
      this.panel.style.bottom = 'auto';
    }
    this.revealPositionedLayout();
    return true;
  }

  renderedImageBox(image) {
    const box = image.getBoundingClientRect();
    const naturalWidth = image.naturalWidth || box.width;
    const naturalHeight = image.naturalHeight || box.height;
    const style = getComputedStyle(image);
    const fit = style.objectFit || 'contain';
    const scale = fit === 'cover'
      ? Math.max(box.width / naturalWidth, box.height / naturalHeight)
      : Math.min(box.width / naturalWidth, box.height / naturalHeight);
    const width = naturalWidth * scale;
    const height = naturalHeight * scale;
    return { left: box.left + (box.width - width) / 2, top: box.top + (box.height - height) / 2, width, height, box, fit };
  }

  lensFallbackSource(image) {
    return image?.currentSrc || image?.src || image?.getAttribute('data_max_resolution') || '';
  }

  lensHighSource(image) {
    return image?.getAttribute('data_max_resolution') || '';
  }

  lensSource(image) {
    const fallback = this.lensFallbackSource(image);
    const high = this.lensHighSource(image);
    if (!high || high === fallback) return fallback;
    if (this.lensReadySources.has(high)) return high;
    this.preloadLensSource(high);
    return fallback;
  }

  warmActiveLensImage() {
    const image = this.activeImage();
    const high = this.lensHighSource(image);
    if (high) this.preloadLensSource(high);
  }

  preloadLensSource(url) {
    if (!url || this.lensReadySources.has(url) || this.lensLoadingSources.has(url)) return;
    const image = new Image();
    image.decoding = 'async';
    image.loading = 'eager';
    const markReady = () => {
      this.lensReadySources.add(url);
      this.lensLoadingSources.delete(url);
      if (!this.isConnected || this.lens?.dataset.active !== 'true') return;
      const active = this.activeImage();
      if (this.lensHighSource(active) === url) this.lens.style.backgroundImage = `url("${url}")`;
    };
    image.onload = () => {
      if (image.decode) image.decode().catch(() => undefined).finally(markReady);
      else markReady();
    };
    image.onerror = () => this.lensLoadingSources.delete(url);
    this.lensLoadingSources.set(url, image);
    image.src = url;
  }

  preloadUltraSource(url) {
    if (!url || this.ultraReadySources.has(url) || this.ultraLoadingSources.has(url)) return;
    const image = new Image();
    image.decoding = 'async';
    image.loading = 'eager';
    const markReady = () => {
      this.ultraReadySources.add(url);
      this.ultraLoadingSources.delete(url);
    };
    image.onload = () => {
      if (image.decode) image.decode().catch(() => undefined).finally(markReady);
      else markReady();
    };
    image.onerror = () => this.ultraLoadingSources.delete(url);
    this.ultraLoadingSources.set(url, image);
    image.src = url;
  }

  orderedUltraSources(priorityIndex = 0) {
    const seen = new Set();
    const count = this.urls.length;
    const ordered = [];
    const add = (index) => {
      if (!count) return;
      const item = this.urls[(index + count) % count];
      if (!item?.url || seen.has(item.url)) return;
      seen.add(item.url);
      ordered.push(item.url);
    };
    add(priorityIndex);
    add(priorityIndex + 1);
    add(priorityIndex - 1);
    this.urls.forEach((_, index) => add(index));
    return ordered;
  }

  queueUltraPreload(priorityIndex = 0) {
    if (!this.urls.length || navigator.connection?.saveData) return;
    if (this.ultraPreloadTimer) window.clearTimeout(this.ultraPreloadTimer);
    const sources = this.orderedUltraSources(priorityIndex);
    this.ultraPreloadTimer = window.setTimeout(() => {
      sources.forEach((url, index) => {
        window.setTimeout(() => {
          if (this.isConnected) this.preloadUltraSource(url);
        }, index < 3 ? 0 : index * 90);
      });
    }, 180);
  }

  onLensDown(event) {
    if (!this.lensEnabled) return;
    if (event.pointerType !== 'touch') return;
    const image = event.target.closest('.product-media-container--image img');
    if (!image) return;
    this.touchLensPointer = event.pointerId;
    this.renderLens(event, image);
  }

  onLensUp(event) {
    if (event.pointerId !== this.touchLensPointer) return;
    this.touchLensPointer = null;
    setTimeout(() => this.hideLens(), 120);
  }

  onLensMove(event) {
    if (!this.lensEnabled || this.dialog?.open || this.panel?.contains(event.target) || event.target.closest('.vcc-precision-tools, .vcc-inspection-trigger, .vcc-precision-counter')) return this.hideLens();
    const image = event.target.closest('.product-media-container--image img');
    if (!image || image.closest('.dialog-zoomed-gallery')) return this.hideLens();
    if (event.pointerType === 'touch' && event.pointerId !== this.touchLensPointer) return;
    this.renderLens(event, image);
  }

  renderLens(event, image) {
    const content = this.renderedImageBox(image);
    const inside = event.clientX >= content.left && event.clientX <= content.left + content.width && event.clientY >= content.top && event.clientY <= content.top + content.height;
    if (!inside) return this.hideLens();
    const size = this.lensSizeMode === 'large' ? Number(this.dataset.lensLarge || 270) : Number(this.dataset.lensSmall || 150);
    const x = event.clientX - content.left;
    const y = event.clientY - content.top;
    const source = this.lensSource(image);
    this.lens.style.setProperty('--vcc-lens-size', `${size}px`);
    this.lens.style.setProperty('--vcc-lens-radius', this.lensShape === 'square' ? '20px' : '50%');
    this.lens.style.left = `${event.clientX}px`;
    this.lens.style.top = `${event.clientY}px`;
    this.lens.style.backgroundImage = `url("${source}")`;
    this.lens.style.backgroundSize = `${content.width * this.zoom}px ${content.height * this.zoom}px`;
    this.lens.style.backgroundPosition = `${size / 2 - x * this.zoom}px ${size / 2 - y * this.zoom}px`;
    this.lens.dataset.active = 'true';
    this.lensBadge.textContent = `${Number.isInteger(this.zoom) ? this.zoom : this.zoom.toFixed(1)}×`;
  }

  onLensWheel(event) {
    if (!this.lensEnabled) return;
    const image = event.target.closest('.product-media-container--image img');
    if (!image || this.dialog?.open) return;
    const content = this.renderedImageBox(image);
    if (event.clientX < content.left || event.clientX > content.left + content.width || event.clientY < content.top || event.clientY > content.top + content.height) return;
    event.preventDefault();
    this.zoom = clamp(Math.round((this.zoom + (event.deltaY < 0 ? .25 : -.25)) * 4) / 4, 2, 8);
    this.syncLensControls();
    this.renderLens(event, image);
  }

  hideLens() { if (this.lens) this.lens.dataset.active = 'false'; }

  syncLensControls() {
    this.querySelectorAll('[data-vcc-action="lens-zoom"]').forEach((button) => button.classList.toggle('is-active', Number(button.dataset.value) === Math.round(this.zoom)));
    this.querySelectorAll('[data-vcc-action="lens-size"]').forEach((button) => button.classList.toggle('is-active', button.dataset.value === this.lensSizeMode));
    this.querySelectorAll('[data-vcc-action="lens-shape"]').forEach((button) => button.classList.toggle('is-active', button.dataset.value === this.lensShape));
    this.querySelectorAll('[data-vcc-action="lens-toggle"]').forEach((button) => {
      button.classList.toggle('is-active', this.lensEnabled);
      button.dataset.state = this.lensEnabled ? 'on' : 'off';
      button.setAttribute('aria-pressed', String(this.lensEnabled));
      button.setAttribute('aria-label', this.lensEnabled ? 'Turn magnifier off' : 'Turn magnifier on');
      const label = button.querySelector('[data-vcc-toggle-label]');
      if (label) label.textContent = 'Magnifier';
      else button.textContent = this.lensEnabled ? 'Magnifier on' : 'Magnifier off';
    });
    const note = this.querySelector('[data-vcc-scroll-note]');
    if (note) note.hidden = true;
    this.querySelectorAll('[data-vcc-panel-note]').forEach((item) => item.hidden = !this.lensEnabled);
    if (this.lensBadge) this.lensBadge.textContent = `${Number.isInteger(this.zoom) ? this.zoom : this.zoom.toFixed(1)}×`;
  }

  updateCounter(index) {
    const counter = this.querySelector('[data-vcc-counter]');
    const count = Number(this.dataset.mediaCount || this.gallery?.querySelectorAll('slideshow-slide.product-media-container').length || 1);
    if (counter) counter.textContent = `${index + 1} / ${count}`;
    const scopes = [this, this.gallery].filter(Boolean);
    scopes.forEach((scope) => scope.querySelectorAll('[data-vcc-thumb-position]').forEach((item) => item.textContent = `${index + 1} / ${count}`));
    const previous = (index - 1 + count) % count;
    const next = (index + 1) % count;
    scopes.forEach((scope) => scope.querySelectorAll('[data-vcc-mobile-current]').forEach((item) => item.textContent = `${index + 1} / ${count}`));
    scopes.forEach((scope) => scope.querySelectorAll('[data-vcc-mobile-prev]').forEach((item) => item.textContent = String(previous + 1).padStart(2, '0')));
    scopes.forEach((scope) => scope.querySelectorAll('[data-vcc-mobile-next]').forEach((item) => item.textContent = String(next + 1).padStart(2, '0')));
  }

  scrollThumbnails(direction) {
    const rail = this.gallery?.querySelector('.slideshow-controls__thumbnails-container');
    if (!rail) return;
    const vertical = matchMedia('(min-width: 750px)').matches && ['left', 'right'].includes(this.gallery.dataset.vccThumbPosition);
    const distance = (vertical ? rail.clientHeight : rail.clientWidth) * .78 * direction;
    rail.scrollBy({ [vertical ? 'top' : 'left']: distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  navigateThumbnail(direction) {
    const thumbnails = [...(this.gallery?.querySelectorAll('.slideshow-controls__thumbnail') || [])].filter((item) => item.offsetParent !== null);
    if (!thumbnails.length) return;
    let index = thumbnails.findIndex((item) => item.getAttribute('aria-selected') === 'true' || item.getAttribute('aria-current') === 'true');
    if (index < 0) index = 0;
    const next = (index + direction + thumbnails.length) % thumbnails.length;
    thumbnails[next]?.click();
    this.alignThumbnailRail(next, next === 0);
  }

  alignThumbnailRail(index, forceStart = false) {
    const rails = [...(this.gallery?.querySelectorAll('.slideshow-controls__thumbnails-container') || [])].filter((item) => item.offsetParent !== null);
    if (!rails.length) {
      const fallback = this.gallery?.querySelector('.slideshow-controls__thumbnails-container');
      if (fallback) rails.push(fallback);
    }
    if (!rails.length) return;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    rails.forEach((rail) => {
      const thumbnails = [...(rail.querySelectorAll('.slideshow-controls__thumbnail') || [])].filter((item) => item.offsetParent !== null);
      if (!thumbnails.length) return;
      if (forceStart || index === 0) {
        this.alignFirstThumbnail(rail, thumbnails[0]);
        return;
      }
      thumbnails[index]?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    });
  }

  alignFirstThumbnail(rail, thumbnail) {
    if (!rail || !thumbnail) return;
    const settle = () => {
      rail.scrollLeft = 0;
      rail.scrollTop = 0;
      const railRect = rail.getBoundingClientRect();
      const thumbRect = thumbnail.getBoundingClientRect();
      if (thumbRect.left < railRect.left) rail.scrollLeft += thumbRect.left - railRect.left - 2;
      if (thumbRect.top < railRect.top) rail.scrollTop += thumbRect.top - railRect.top - 2;
      const settledThumbRect = thumbnail.getBoundingClientRect();
      if (settledThumbRect.right > railRect.right) rail.scrollLeft += settledThumbRect.right - railRect.right + 2;
      if (settledThumbRect.bottom > railRect.bottom) rail.scrollTop += settledThumbRect.bottom - railRect.bottom + 2;
    };
    settle();
    requestAnimationFrame(settle);
    setTimeout(settle, 120);
    setTimeout(settle, 360);
  }

  openUltra(button) {
    if (!this.urls.length) return;
    this.launcher = button;
    const mediaId = this.activeImage()?.closest('.product-media')?.dataset.mediaId;
    const found = this.urls.findIndex((item) => String(item.id) === String(mediaId));
    this.setUltraImage(found >= 0 ? found : 0);
    const wasOpen = this.dialog?.open;
    if (!wasOpen) this.dialog.showModal();
    if (!wasOpen) this.pushUltraHistory();
    this.queueUltraPreload(found >= 0 ? found : 0);
  }

  pushUltraHistory() {
    if (this.ultraHistoryOpen || !window.history?.pushState) return;
    try {
      const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
      window.history.pushState({ ...currentState, vccUltraZoom: true }, '', window.location.href);
      this.ultraHistoryOpen = true;
    } catch (error) {
      this.ultraHistoryOpen = false;
    }
  }

  closeUltra(options = {}) {
    if (!this.dialog?.open) return;
    this.ultraSkipHistoryClose = Boolean(options.skipHistory);
    if (document.fullscreenElement && this.ultraShell?.contains(document.fullscreenElement)) {
      document.exitFullscreen?.().catch?.(() => {});
    }
    this.dialog.close();
  }

  setUltraImage(index) {
    if (!this.urls.length) return;
    this.imageIndex = (index + this.urls.length) % this.urls.length;
    const item = this.urls[this.imageIndex];
    this.resetUltra();
    this.ultraImage.onload = () => {
      this.ultraReadySources.add(item.url);
      this.ultraLoadingSources.delete(item.url);
      this.querySelector('[data-vcc-ultra-meta]').textContent = `${this.ultraImage.naturalWidth} × ${this.ultraImage.naturalHeight} px · wheel or pinch to zoom · drag to pan`;
      this.renderUltra();
    };
    this.ultraImage.onerror = () => this.ultraLoadingSources.delete(item.url);
    this.preloadUltraSource(item.url);
    this.ultraImage.src = item.url;
    this.ultraImage.alt = item.alt || '';
    const ultraTitle = this.querySelector('[data-vcc-ultra-title]');
    if (ultraTitle) ultraTitle.textContent = matchMedia('(max-width: 749px)').matches
      ? `Ultra Zoom · Image ${this.imageIndex + 1} of ${this.urls.length} · pinch to zoom · drag to pan`
      : `Ultra Zoom · Image ${this.imageIndex + 1} of ${this.urls.length}`;
    this.querySelectorAll('[data-vcc-action="ultra-thumb"]').forEach((button) => {
      button.classList.toggle('is-active', Number(button.dataset.index || 0) === this.imageIndex);
    });
  }

  setUltraScale(scale, origin) {
    const previousScale = this.scale || 1;
    const nextScale = clamp(scale, 1, 16);
    if (origin && this.viewport && nextScale !== previousScale) {
      const rect = this.viewport.getBoundingClientRect();
      const originX = origin.clientX - rect.left - rect.width / 2;
      const originY = origin.clientY - rect.top - rect.height / 2;
      const ratio = nextScale / previousScale;
      this.panX = this.panX * ratio + originX * (1 - ratio);
      this.panY = this.panY * ratio + originY * (1 - ratio);
    }
    this.scale = nextScale;
    if (this.scale === 1) { this.panX = 0; this.panY = 0; }
    this.renderUltra();
    this.querySelectorAll('[data-vcc-action="ultra-zoom"]').forEach((button) => button.classList.toggle('is-active', Number(button.dataset.scale) === this.scale));
  }

  resetUltra() {
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    this.renderUltra();
  }

  renderUltra() {
    if (!this.ultraImage) return;
    this.ultraImage.style.setProperty('--vcc-ultra-scale', this.scale);
    this.ultraImage.style.setProperty('--vcc-pan-x', `${this.panX}px`);
    this.ultraImage.style.setProperty('--vcc-pan-y', `${this.panY}px`);
    const readout = this.querySelector('[data-vcc-ultra-readout]');
    if (readout) readout.textContent = `${Math.round(this.scale * 100)}%`;
  }

  onUltraWheel(event) {
    event.preventDefault();
    this.setUltraScale(this.scale * (event.deltaY < 0 ? 1.13 : .885), { clientX: event.clientX, clientY: event.clientY });
  }

  onUltraPointerDown(event) {
    if (event.target.closest('button')) return;
    event.preventDefault();
    this.ultraPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.viewport.setPointerCapture(event.pointerId);
    if (this.ultraPointers.size === 1) {
      this.dragStart = { x: event.clientX - this.panX, y: event.clientY - this.panY };
      this.viewport.dataset.dragging = 'true';
    } else if (this.ultraPointers.size === 2) {
      const points = [...this.ultraPointers.values()];
      this.pinchStart = { distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y), scale: this.scale };
      this.dragStart = null;
    }
  }

  onUltraPointerMove(event) {
    if (!this.ultraPointers.has(event.pointerId)) return;
    this.ultraPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.ultraPointers.size === 2 && this.pinchStart) {
      const points = [...this.ultraPointers.values()];
      const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      this.setUltraScale(this.pinchStart.scale * distance / Math.max(1, this.pinchStart.distance), {
        clientX: (points[0].x + points[1].x) / 2,
        clientY: (points[0].y + points[1].y) / 2
      });
      return;
    }
    if (this.ultraPointers.size === 1 && this.dragStart) {
      this.panX = event.clientX - this.dragStart.x;
      this.panY = event.clientY - this.dragStart.y;
      this.renderUltra();
    }
  }

  onUltraPointerUp(event) {
    this.ultraPointers.delete(event.pointerId);
    if (this.ultraPointers.size === 1) {
      const point = [...this.ultraPointers.values()][0];
      this.pinchStart = null;
      this.dragStart = { x: point.x - this.panX, y: point.y - this.panY };
      this.viewport.dataset.dragging = 'true';
      return;
    }
    if (!this.ultraPointers.size) {
      this.dragStart = null;
      this.pinchStart = null;
      this.viewport.dataset.dragging = 'false';
    }
  }

  applyDetail() {
    const baseContrast = Number(this.dataset.detailContrast || 112) / 100;
    const baseSaturation = Number(this.dataset.detailSaturation || 108) / 100;
    const baseBrightness = Number(this.dataset.detailBrightness || 102) / 100;
    this.ultraImage.style.setProperty('--vcc-detail-contrast', 1 + (baseContrast - 1) * this.detailStrength);
    this.ultraImage.style.setProperty('--vcc-detail-saturation', 1 + (baseSaturation - 1) * this.detailStrength);
    this.ultraImage.style.setProperty('--vcc-detail-brightness', 1 + (baseBrightness - 1) * this.detailStrength);
    this.ultraImage.dataset.detail = String(this.detailEnabled);
  }

  cycleBackground(button) {
    const backgrounds = ['light', 'dark', 'checker'];
    this.backgroundIndex = (this.backgroundIndex + 1) % backgrounds.length;
    this.viewport.dataset.bg = backgrounds[this.backgroundIndex];
    button.textContent = `Background: ${backgrounds[this.backgroundIndex]}`;
  }

  async toggleFullscreen() {
    try {
      if (!document.fullscreenElement) await this.ultraShell.requestFullscreen();
      else await document.exitFullscreen();
    } catch (error) { console.warn('[VCC Precision] Fullscreen unavailable', error); }
  }

  updateStampPreview() {
    const preview = this.querySelector('[data-vcc-stamp-preview]');
    if (!preview) return;
    preview.style.setProperty('--vcc-stamp-preview-size', `${Number(this.dataset.logoSize || 18)}%`);
    preview.style.setProperty('--vcc-stamp-opacity', Number(this.dataset.logoOpacity || 72) / 100);
    const position = this.dataset.logoPosition || 'auto';
    preview.style.left = '';
    preview.style.right = '';
    preview.style.top = '';
    preview.style.bottom = '';
    preview.style.transform = '';
    if (position === 'top-left') { preview.style.left = '3%'; preview.style.top = '3%'; }
    else if (position === 'top-right') { preview.style.right = '3%'; preview.style.top = '3%'; }
    else if (position === 'bottom-left') { preview.style.left = '3%'; preview.style.bottom = '3%'; }
    else if (position === 'bottom-center') { preview.style.left = '50%'; preview.style.bottom = '3%'; preview.style.transform = 'translateX(-50%)'; }
    else { preview.style.right = '3%'; preview.style.bottom = '3%'; }
  }

  sizeInfoDocument() {
    const template = this.querySelector('[data-vcc-size-info-template]');
    return new DOMParser().parseFromString(template?.innerHTML || '', 'text/html');
  }

  sizeInfoRows() {
    const doc = this.sizeInfoDocument();
    const rows = [...doc.querySelectorAll('table tr')].map((row) => [...row.children].map((cell) => cell.textContent.replace(/\s+/g, ' ').trim()).filter(Boolean)).filter((cells) => cells.length);
    return rows.slice(0, 8);
  }

  toggleSizeInfo(button) {
    const card = this.querySelector('[data-vcc-size-card]');
    if (!card) return;
    const open = card.hidden;
    if (open && !card.dataset.ready) {
      const rows = this.sizeInfoRows();
      const note = [...this.sizeInfoDocument().body.querySelectorAll('p, div, small, span')]
        .map((node) => node.textContent.replace(/\s+/g, ' ').trim())
        .find((text) => /see\s+sizing\s+chart|signs\s+of\s+wear|see\s+photo/i.test(text));
      card.innerHTML = '<strong>Size Information</strong>' +
        (this.dataset.productTitle ? '<p class="vcc-ultra__size-title">' + this.escapeHtml(this.dataset.productTitle) + '</p>' : '') +
        (rows.length ? '<table>' + rows.map((cells) => '<tr>' + cells.map((cell) => '<td>' + this.escapeHtml(cell) + '</td>').join('') + '</tr>').join('') + '</table>' : '<p>No size chart found for this product.</p>') +
        (note ? '<small>' + this.escapeHtml(note) + '</small>' : '');
      card.dataset.ready = 'true';
    }
    card.hidden = !open;
    button.classList.toggle('is-active', open);
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  wrapCanvasText(context, text, x, y, maxWidth, lineHeight, maxLines) {
    const words = String(text || '').replace(/\s+/g, ' ').trim().split(' ');
    let line = '';
    let lines = 0;
    for (let index = 0; index < words.length; index += 1) {
      const test = line ? line + ' ' + words[index] : words[index];
      if (context.measureText(test).width > maxWidth && line) {
        context.fillText(line, x, y);
        y += lineHeight;
        line = words[index];
        lines += 1;
        if (maxLines && lines >= maxLines) return y;
      } else {
        line = test;
      }
    }
    if (line && (!maxLines || lines < maxLines)) {
      context.fillText(line, x, y);
      y += lineHeight;
    }
    return y;
  }

  async loadImageForCanvas(url) {
    const response = await fetch(url, { mode: 'cors', credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Image request failed: ${response.status}`);
    const objectUrl = URL.createObjectURL(await response.blob());
    const image = new Image();
    image.src = objectUrl;
    await image.decode();
    return { image, release: () => URL.revokeObjectURL(objectUrl) };
  }

  brightestPosition(context, width, height) {
    const sample = Math.max(16, Math.floor(Math.min(width, height) * .06));
    const margin = Math.max(4, Math.floor(Math.min(width, height) * .025));
    const points = [
      ['top-left', margin, margin], ['top-right', width - sample - margin, margin],
      ['bottom-left', margin, height - sample - margin], ['bottom-right', width - sample - margin, height - sample - margin],
      ['bottom-center', Math.floor(width / 2 - sample / 2), height - sample - margin]
    ];
    return points.map(([position, x, y]) => {
      const data = context.getImageData(clamp(x, 0, width - sample), clamp(y, 0, height - sample), sample, sample).data;
      let brightness = 0;
      for (let index = 0; index < data.length; index += 16) brightness += data[index] * .2126 + data[index + 1] * .7152 + data[index + 2] * .0722;
      return { position, brightness };
    }).sort((a, b) => b.brightness - a.brightness)[0].position;
  }

  stampPosition(position, width, height, stampWidth, stampHeight, margin) {
    if (position === 'top-left') return [margin, margin];
    if (position === 'top-right') return [width - stampWidth - margin, margin];
    if (position === 'bottom-left') return [margin, height - stampHeight - margin];
    if (position === 'bottom-center') return [(width - stampWidth) / 2, height - stampHeight - margin];
    return [width - stampWidth - margin, height - stampHeight - margin];
  }

  async saveImage(button, includeInfo = false) {
    if (!this.dataset.logo || !this.urls[this.imageIndex]) return;
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Preparing…';
    let source;
    let logo;
    try {
      source = await this.loadImageForCanvas(this.urls[this.imageIndex].url);
      logo = await this.loadImageForCanvas(this.dataset.logo);
      const imageWidth = source.image.naturalWidth;
      const imageHeight = source.image.naturalHeight;
      const panelWidth = includeInfo ? Math.max(760, Math.round(imageWidth * .42)) : 0;
      const canvas = document.createElement('canvas');
      canvas.width = imageWidth + panelWidth;
      canvas.height = imageHeight;
      const context = canvas.getContext('2d', { alpha: false });
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(source.image, 0, 0);
      const stampWidth = imageWidth * (Number(this.dataset.logoSize || 18) / 100);
      const stampHeight = stampWidth * (logo.image.naturalHeight / logo.image.naturalWidth);
      const margin = imageWidth * .025;
      let position = this.dataset.logoPosition || 'auto';
      if (position === 'auto') position = this.brightestPosition(context, imageWidth, imageHeight);
      const [x, y] = this.stampPosition(position, imageWidth, imageHeight, stampWidth, stampHeight, margin);
      context.globalAlpha = Number(this.dataset.logoOpacity || 72) / 100;
      context.drawImage(logo.image, x, y, stampWidth, stampHeight);
      context.globalAlpha = 1;
      if (includeInfo) {
        const panelX = imageWidth;
        const pad = Math.max(42, Math.round(panelWidth * .06));
        let yPos = pad;
        context.fillStyle = '#f6f7f9';
        context.fillRect(panelX, 0, panelWidth, imageHeight);
        context.fillStyle = '#111';
        context.font = `800 ${Math.max(30, Math.round(panelWidth * .045))}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        yPos = this.wrapCanvasText(context, this.dataset.productTitle || document.title, panelX + pad, yPos + 10, panelWidth - pad * 2, Math.round(panelWidth * .058), 4);
        context.font = `800 ${Math.max(24, Math.round(panelWidth * .034))}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        if (this.dataset.productPrice) {
          yPos += 24;
          context.fillText(this.dataset.productPrice, panelX + pad, yPos);
        }
        yPos += 46;
        context.font = `800 ${Math.max(18, Math.round(panelWidth * .026))}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        context.fillText('Sizing Information', panelX + pad, yPos);
        yPos += 24;
        context.font = `600 ${Math.max(16, Math.round(panelWidth * .022))}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        this.sizeInfoRows().forEach((cells) => {
          const rowHeight = Math.max(38, Math.round(panelWidth * .05));
          context.strokeStyle = '#d6d9de';
          context.strokeRect(panelX + pad, yPos, panelWidth - pad * 2, rowHeight);
          context.fillText(cells[0] || '', panelX + pad + 18, yPos + rowHeight * .65);
          context.fillText(cells.slice(1).join(' ') || '', panelX + panelWidth * .58, yPos + rowHeight * .65);
          yPos += rowHeight;
        });
      }
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', .94));
      if (!blob) throw new Error('Image export failed');
      const anchor = document.createElement('a');
      anchor.href = URL.createObjectURL(blob);
      anchor.download = `${this.dataset.productHandle || 'product'}-image-${this.imageIndex + 1}-${includeInfo ? 'product-info-' : ''}stamped.jpg`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(anchor.href), 1000);
      button.textContent = 'Downloaded';
    } catch (error) {
      console.error('[VCC Precision] Stamped image export failed', error);
      button.textContent = 'Stamp unavailable';
    } finally {
      source?.release();
      logo?.release();
      setTimeout(() => { button.disabled = false; button.textContent = original; }, 1600);
    }
  }

  onKey(event) {
    if (!this.dialog?.open) return;
    if (event.key === 'Escape') this.closeUltra();
    if (event.key === 'ArrowLeft') this.setUltraImage(this.imageIndex - 1);
    if (event.key === 'ArrowRight') this.setUltraImage(this.imageIndex + 1);
    if (event.key === '+' || event.key === '=') this.setUltraScale(this.scale * 1.25);
    if (event.key === '-') this.setUltraScale(this.scale / 1.25);
  }
}

if (!customElements.get('vcc-precision-gallery')) customElements.define('vcc-precision-gallery', VccPrecisionGallery);
